import { useState, useCallback, memo } from 'react';
import { fruitsResumeData, fruitsIcon } from './constants';
import { AnimatedHand } from './AnimatedHand';
import { Resume } from './Resume';
import { InterButtons } from './InterButtons';
import AcceptedIcon from '../../assets/accept.svg';
import DeclinedFIcon from '../../assets/declinedF.svg';
import ClearStampIcon from '../../assets/clear.svg';
import DoubtStampIcon from '../../assets/doubt.svg';
import { ResultView } from './ResultView';
import { useAppContext } from '../../context';
import { AnimatedSprite } from '../AnimatedSprite';
import { usebubnezSound } from '../../hooks/useBubnez';


interface IInterview {
    stage: number;
    setStage: (val: number) => void;
    onLastResult: () => void;
    instructionOpened: boolean;
}

export const Interview = memo(({ stage, setStage, onLastResult, instructionOpened }: IInterview) => {
    const [animationState, setAnimationState] = useState({
        isAnimating: false,
        showStamp: false,
        showResult: false,
        isAccepted: false,
        hide: false
    });

    const { fruitType, gameResult, gameStarted } = useAppContext();
    const data = fruitsResumeData[fruitType];
    const { playbubnezSound } = usebubnezSound(fruitType);

    const handleDecision = useCallback((isAccepted: boolean) => {
        if (!animationState.isAnimating) {
            setAnimationState(prev => ({
                ...prev,
                isAnimating: true,
                isAccepted
            }));
        }
    }, [animationState.isAnimating]);

    const handleAnimationComplete = useCallback(() => {
        setAnimationState(prev => ({
            ...prev,
            isAnimating: false,
            showStamp: false,
            showResult: true
        }));
    }, []);

    const handleResultButton = useCallback(() => {
        if (stage === 5) {
            return onLastResult();
        }
        setAnimationState(prev => ({ ...prev, showResult: false, hide: true }));
        setTimeout(() => {
            setStage(stage + 1)
            setAnimationState(prev => ({
                ...prev,
                showStamp: false,
                showResult: false,
                isAnimating: false,
                hide: false
            }));
        }, 500);
    }, [stage, onLastResult, setStage]);

    return (
        <div className="relative w-full h-full">
            <div
                className={`
                    absolute inset-0 bg-custom-gradient transition-opacity duration-300
                    ${gameResult || !gameStarted ? 'opacity-100 z-10' : 'opacity-0 -z-10'}
                `}
            />

            <div className={`
                absolute w-full transition-all duration-300
                ${!instructionOpened || gameResult
                    ? 'bottom-[-20px] -rotate-12 h-[300px] -z-10'
                    : 'h-full top-0'
                }
            `}>
                <AnimatedHand
                    isAnimating={animationState.isAnimating}
                    onAnimationComplete={handleAnimationComplete}
                    setShowStamp={(val) => setAnimationState(prev => ({ ...prev, showStamp: val }))}
                />

                <div className="flex flex-col items-center justify-between pb-11 mx-auto max-w-[calc(100%-32px)] h-full">
                    <div className="flex items-center justify-center w-full pt-10 grow">
                        <div className="relative w-full max-w-[358px] h-full max-h-[454px] bg-white shadow-cmodal p-6 pt-16 my-8">
                            <div
                                className={`
                                    absolute -top-10 left-1/2 -translate-x-1/2 bg-cgrey text-white z-20 text-center
                                    ${!instructionOpened || gameResult
                                        ? 'w-[124px] h-[42px] -top-5'
                                        : 'p-4'
                                    }
                                `}
                            >
                                {instructionOpened && !gameResult && (
                                    <>
                                        <p className="font-bold font-z text-2xl">Резюме</p>
                                        <p className="text-xl">
                                            <span className='font-bold'>{stage}</span> / 5
                                        </p>
                                    </>
                                )}
                            </div>

                            <div className="h-[144px] w-full flex relative">
                                <div className={`
                                    absolute w-full transition-opacity duration-300
                                    ${animationState.showResult && !animationState.hide ? 'opacity-100' : 'opacity-0 pointer-events-none'}
                                `}>
                                    <div className="h-[144px] w-full flex justify-center relative flex-none">
                                        <img
                                            src={`/images/interview/${fruitType}/${stage}.webp`}
                                            alt={data.name}
                                            className="w-[144px] h-[144px] rounded-full"
                                        />
                                        <img
                                            src={data.forms[stage - 1].isAcceptCorret ? AcceptedIcon : DeclinedFIcon}
                                            alt='Штамп'
                                            className={`absolute top-0 z-20 w-[122px] h-[59px] ${data.forms[stage - 1].isAcceptCorret ? 'left-0' : 'right-0'}`}
                                        />
                                    </div>
                                </div>

                                <div className={`
                                    absolute w-full transition-opacity duration-300
                                    ${!animationState.showResult && !animationState.hide ? 'opacity-100' : 'opacity-0 pointer-events-none'}
                                `}>
                                    <div className="h-[144px] w-full flex relative">
                                        <div
                                            onClick={() => playbubnezSound(stage)}
                                            className="cursor-pointer"
                                            role="button"
                                        >
                                            <AnimatedSprite
                                                spriteUrl={`/images/interview/sprites/${fruitType}/${stage}.webp`}
                                                className="w-[146px] h-[146px] rounded-full"
                                                frameCount={4}
                                                frameWidth={146}
                                            />
                                        </div>
                                        <div className="ml-3 mr-4">
                                            <h2 className="mt-4 text-[16px]/[16px] xxs:text-[20px]/[20px] font-villula mb-4 text-cgrey">
                                                {data.forms[stage - 1].name}
                                            </h2>
                                            <img
                                                src={animationState.isAccepted ? ClearStampIcon : DoubtStampIcon}
                                                alt='Штамп'
                                                className={`transition-opacity duration-300 w-[122px] h-[59px] ${animationState.showStamp ? 'opacity-100' : 'opacity-0'}`}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Контент резюме/результата */}
                            <div className="mt-6 relative min-h-[200px]">
                                <div className={`
        absolute w-full transition-opacity duration-300
        ${!animationState.showResult && !animationState.hide ? 'opacity-100' : 'opacity-0'} 
    `}>
                                    <Resume data={data.forms[stage - 1]} />
                                </div>
                                <div className={`
        absolute w-full transition-opacity duration-300  
        ${animationState.showResult && !animationState.hide ? 'opacity-100' : 'opacity-0'}
    `}>
                                    <ResultView
                                        data={!animationState.isAccepted && data.forms[stage - 1]?.decline
                                            ? data.forms[stage - 1].decline
                                            : data.forms[stage - 1].accept
                                        }
                                    />
                                </div>
                            </div>

                            {/* Декоративная иконка */}
                            <img
                                className={`${animationState.hide ? 'opacity-0' : 'opacity-100'} absolute z-10 transition-opacity duration-300 ${fruitsIcon[stage - 1].iconPos}`}
                                src={fruitsIcon[stage - 1].img || fruitsIcon[stage - 1].icon}
                                alt='Скрепка'
                            />
                        </div>
                    </div>

                    {/* Кнопки */}
                    <div className="w-full mt-auto z-20">
                        <InterButtons
                            disabled={animationState.isAnimating}
                            onButton={handleDecision}
                            onResultButton={handleResultButton}
                            isResult={animationState.showResult}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
});